import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Typography, IconButton, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField, Alert, Tooltip, Badge as UnreadBadge,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon, People, Business, DirectionsCar,
  Badge, ListAlt, Logout, Dashboard, Map, Monitor,
  Security, Email, Place, Gavel, LocalShipping, Assessment,
  Category, LocalFireDepartment, PriorityHigh, History,
  VpnKey, NotificationsActive, QuestionAnswer, Schedule, Headset, Chat,
  Fullscreen, FullscreenExit, Layers, Tv,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import useCADWebSocket from '../hooks/useCADWebSocket';
import { formatTime24, ensureTimezone } from '../utils/time';
import { playSound } from '../utils/sounds';
import { isChildPopup } from '../utils/popup';
import { OnlineCounts } from '../utils/presence';
import api from '../api/client';

const DRAWER_WIDTH = 240;

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [clock, setClock] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const popupRefs = useRef<Record<string, Window | null>>({});
  const isPopup = isChildPopup();
  const isDispatchRoute = location.pathname.startsWith('/dispatch/');
  const isMdtRoute = location.pathname.includes('mobile/mdt');

  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [chatUnread, setChatUnread] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [online, setOnline] = useState<OnlineCounts>({ dispatch_users: 0, mdt_users: 0, total_users: 0 });

  const chatSoundRef = useRef('notification');

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    onFsChange();
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  const handleShellWsEvent = React.useCallback((event: string, data?: any) => {
    if (event === 'presence_changed' && data) {
      setOnline({
        dispatch_users: data.dispatch_users ?? 0,
        mdt_users: data.mdt_users ?? 0,
        total_users: data.total_users ?? 0,
      });
      return;
    }
    if (event !== 'chat_message') return;
    // Track unread dispatch chat messages while the chat window isn't the active view.
    if (location.pathname !== '/dispatch/chat') {
      setChatUnread((n) => n + 1);
    }
    // Play the configured chat sound once (main window only) for messages from others.
    const isOwn = user?.username && data?.username && data.username === user.username;
    if (!window.opener && !isOwn && chatSoundRef.current && chatSoundRef.current !== 'none') {
      playSound(chatSoundRef.current);
    }
  }, [location.pathname, user?.username]);
  const noop = React.useCallback(() => {}, []);
  // Register the shell's presence connection under the area actually being
  // viewed. The MDT view is nested inside AppShell, so hardcoding 'dispatch'
  // here made every MDT user also appear online under Dispatch.
  useCADWebSocket(noop, isMdtRoute ? 'mobile' : 'dispatch', undefined, handleShellWsEvent);

  const handleChangePassword = async () => {
    setPwError('');
    setPwSuccess('');
    if (!pwCurrent || !pwNew) { setPwError('All fields are required'); return; }
    if (pwNew.length < 6) { setPwError('New password must be at least 6 characters'); return; }
    if (pwNew !== pwConfirm) { setPwError('New passwords do not match'); return; }
    try {
      await api.post('/auth/change-password', { current_password: pwCurrent, new_password: pwNew });
      setPwSuccess('Password changed successfully');
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
      setTimeout(() => { setPwOpen(false); setPwSuccess(''); }, 1500);
    } catch (err: any) {
      setPwError(err.response?.data?.detail || 'Failed to change password');
    }
  };

  useEffect(() => {
    let id: ReturnType<typeof setInterval>;
    ensureTimezone().then(() => {
      const tick = () => setClock(formatTime24(new Date()));
      tick();
      id = setInterval(tick, 1000);
    });
    return () => { if (id) clearInterval(id); };
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => api.get('/online').then((r) => { if (alive) setOnline(r.data); }).catch(() => {});
    load();
    // Fallback refresh in case a presence_changed event is missed.
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    api.get('/system-config/logo', { responseType: 'blob' })
      .then(() => { setLogoUrl('/api/system-config/logo'); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/system-config/public')
      .then((r) => { if (r.data.sound_dispatch_chat) chatSoundRef.current = r.data.sound_dispatch_chat; })
      .catch(() => {});
  }, []);

  // In a popup window (Map/Units/Chat/etc.), persist its size & position so it reopens where the user left it.
  useEffect(() => {
    if (!window.opener || !window.name) return;
    const key = `wc_popup_geom_${window.name}`;
    const saveGeom = () => {
      try {
        localStorage.setItem(key, JSON.stringify({
          width: window.outerWidth,
          height: window.outerHeight,
          left: window.screenX,
          top: window.screenY,
        }));
      } catch { /* ignore quota/serialization errors */ }
    };
    let t: ReturnType<typeof setTimeout>;
    const onResize = () => { clearTimeout(t); t = setTimeout(saveGeom, 400); };
    window.addEventListener('resize', onResize);
    window.addEventListener('beforeunload', saveGeom);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('beforeunload', saveGeom);
    };
  }, []);

  const menuItems: { label: string; icon: React.ReactElement; path: string; popup?: string }[] = [];

  if (user?.mobile_access) {
    menuItems.push(
      { label: 'Mobile MDT', icon: <LocalShipping />, path: '/mobile/mdt' },
    );
  }

  if (user?.dispatch_access) {
    menuItems.push(
      { label: 'Home', icon: <Dashboard />, path: '/dispatch/home' },
      { label: 'Map / Dispatch', icon: <Map />, path: '/dispatch/map', popup: 'wc_map' },
      { label: 'Units & Calls', icon: <Monitor />, path: '/dispatch/units', popup: 'wc_units' },
      { label: 'Log', icon: <History />, path: '/dispatch/log' },
      { label: 'Call History', icon: <Assessment />, path: '/dispatch/history', popup: 'wc_history' },
      { label: 'Scheduled Calls', icon: <Schedule />, path: '/dispatch/scheduled' },
      { label: 'Chat', icon: <Chat />, path: '/dispatch/chat', popup: 'wc_chat' },
      { label: "Who's Online", icon: <People />, path: '/dispatch/online' },
    );
  }

  if (user?.admin_access || user?.superadmin_access) {
    menuItems.push(
      { label: 'Agencies', icon: <Business />, path: '/admin/agencies' },
      { label: 'Units', icon: <DirectionsCar />, path: '/admin/units' },
      { label: 'Personnel', icon: <Badge />, path: '/admin/personnel' },
      { label: 'Call Types', icon: <ListAlt />, path: '/admin/call-types' },
      { label: 'Status Types', icon: <Dashboard />, path: '/admin/status-types' },
      { label: 'Users', icon: <People />, path: '/admin/users' },
      { label: 'Locations', icon: <Place />, path: '/admin/locations' },
      { label: 'Destinations', icon: <LocalShipping />, path: '/admin/destinations' },
      { label: 'Dispositions', icon: <Gavel />, path: '/admin/dispositions' },
      { label: 'Agency Types', icon: <Category />, path: '/admin/agency-types' },
      { label: 'Unit Types', icon: <LocalFireDepartment />, path: '/admin/unit-types' },
      { label: 'Call Priorities', icon: <PriorityHigh />, path: '/admin/call-priorities' },
      { label: 'Service Zones (ESZ)', icon: <Layers />, path: '/admin/zones' },
      { label: 'GIS / Mapping', icon: <Map />, path: '/admin/gis' },
      { label: 'Status Boards', icon: <Tv />, path: '/admin/status-boards' },
      { label: 'Reports', icon: <Assessment />, path: '/admin/reports' },
      { label: 'Active911', icon: <NotificationsActive />, path: '/admin/active911' },
      { label: 'Case Entry', icon: <QuestionAnswer />, path: '/admin/case-entry' },
      { label: 'Audit Log', icon: <History />, path: '/admin/audit-log' },
      { label: 'Chat Log', icon: <Chat />, path: '/admin/chat-log' },
      { label: 'Operator Positions', icon: <Headset />, path: '/admin/operator-positions' },
      { label: 'SMTP / Email', icon: <Email />, path: '/admin/smtp' },
    );
  }

  if (user?.superadmin_access) {
    menuItems.push(
      { label: 'System Config', icon: <SettingsIcon />, path: '/superadmin/config' },
      { label: 'Password Policy', icon: <Security />, path: '/superadmin/security' },
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: '#1a1a2e' }}>
        <Toolbar>
          {logoUrl && (
            <Box component="img" src={logoUrl} alt="Agency Logo" sx={{ height: 36, maxWidth: 120, objectFit: 'contain', mr: 1.5 }} />
          )}
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700 }}>
            WatchCenter CAD
          </Typography>
          <Typography variant="body2" sx={{ mr: 2, fontFamily: 'monospace', fontWeight: 700, fontSize: '2rem', letterSpacing: 2, color: '#64b5f6' }}>
            {clock}
          </Typography>
          {user?.dispatch_access && !isMdtRoute && (
            <Tooltip title={`Logged in now — Dispatch: ${online.dispatch_users}, MDT: ${online.mdt_users}, Total: ${online.total_users}`}>
              <Chip
                icon={<People />}
                label={`${online.dispatch_users} / ${online.mdt_users}`}
                size="small"
                sx={{ mr: 2, color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '& .MuiChip-icon': { color: '#64b5f6' } }}
              />
            </Tooltip>
          )}
          {isMdtRoute && (
            <Tooltip title={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}>
              <IconButton color="inherit" onClick={toggleFullscreen} sx={{ mr: 1 }}>
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          )}
          <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
          <Typography variant="body2" sx={{ mr: 2 }}>{user?.username}</Typography>
          <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
          <Typography variant="body2" sx={{ mr: 2 }}>{user?.full_name}</Typography>
          <Tooltip title="Change Password">
            <IconButton color="inherit" onClick={() => { setPwOpen(true); setPwError(''); setPwSuccess(''); setPwCurrent(''); setPwNew(''); setPwConfirm(''); }}>
              <VpnKey />
            </IconButton>
          </Tooltip>
          <IconButton color="inherit" onClick={() => { logout(); if (isPopup) { window.close(); } else if (location.pathname.includes('mobile/mdt')) { window.location.href = '/mdt'; } else { navigate('/login'); } }}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>
      {!(isPopup && isDispatchRoute) && !isMdtRoute && (
        <Drawer variant="permanent" sx={{
          width: DRAWER_WIDTH, flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}>
          <Toolbar />
          <List>
            {menuItems.map((item, idx) => (
              <React.Fragment key={item.path}>
                {idx > 0 && menuItems[idx - 1].path.split('/')[1] !== item.path.split('/')[1] && <Divider />}
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => {
                    if (item.path === '/dispatch/chat') setChatUnread(0);
                    if (item.popup) {
                      const existing = popupRefs.current[item.popup];
                      if (existing && !existing.closed) {
                        existing.focus();
                      } else {
                        let width = window.screen.width;
                        let height = window.screen.height;
                        let left = 0;
                        let top = 0;
                        try {
                          const saved = localStorage.getItem(`wc_popup_geom_${item.popup}`);
                          if (saved) {
                            const g = JSON.parse(saved);
                            if (Number.isFinite(g.width) && g.width > 200) width = g.width;
                            if (Number.isFinite(g.height) && g.height > 200) height = g.height;
                            if (Number.isFinite(g.left)) left = g.left;
                            if (Number.isFinite(g.top)) top = g.top;
                          }
                        } catch { /* ignore malformed geometry */ }
                        popupRefs.current[item.popup] = window.open(
                          item.path,
                          item.popup,
                          `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`,
                        );
                      }
                    } else {
                      navigate(item.path);
                    }
                  }}
                >
                  <ListItemIcon>
                    {item.path === '/dispatch/chat' && chatUnread > 0
                      ? <UnreadBadge badgeContent={chatUnread} color="error">{item.icon}</UnreadBadge>
                      : item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </Drawer>
      )}
      <Box component="main" sx={{ flexGrow: 1, p: (isPopup && isDispatchRoute) || isMdtRoute ? 1 : 3 }}>
        <Toolbar />
        <Outlet context={{ online }} />
      </Box>

      <Dialog open={pwOpen} onClose={() => setPwOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {pwError && <Alert severity="error" sx={{ mb: 1 }}>{pwError}</Alert>}
          {pwSuccess && <Alert severity="success" sx={{ mb: 1 }}>{pwSuccess}</Alert>}
          <TextField fullWidth label="Current Password" type="password" margin="normal" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} autoFocus />
          <TextField fullWidth label="New Password" type="password" margin="normal" value={pwNew} onChange={(e) => setPwNew(e.target.value)} />
          <TextField fullWidth label="Confirm New Password" type="password" margin="normal" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleChangePassword(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword}>Change Password</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
